/**
 * TypeKitEditor — the "Needs before it starts" section of ONE milestone type's page in Templates →
 * Milestone (docs/OPERATING_MODEL.md build steps 6a + 6b).
 *
 * 6a moved the kit onto the type's page and retired the Gates tab. 6b (2026-09-24) made the kit a
 * list of SHARED PROJECT FACTS (database/270_kit_facts.sql): a fact comes from one firm list, any
 * type can need it, and once a project has it in hand it counts for every milestone that needs it.
 * So adding an item can reuse an existing fact, and rewording a fact rewords it for every type that
 * uses it (the row says how many). Every type can have a kit now — it no longer depends on the type
 * being in the WBS Milestones list. A feeder carries no kit.
 *
 * (File name kept from the retired Gates editor so imports stay put.)
 */

import { useEffect, useState } from 'react'
import { confirmDialog, promptDialog, alertDialog } from './Dialog.jsx'
import {
  getTypeKit, getFactLibrary, saveTypeKit, deleteTypeKit, addKitFact, removeKitFact, updateKitFact, reorderKit,
  GATE_SOURCE_LABELS, GATE_SOURCE_KINDS,
} from '../services/gates.js'
import { listMilestoneTypeSettings } from '../services/milestoneTypes.js'
import Select from './Select.jsx'

export function TypeKitEditor({ typeId, typeName }) {
  const [data, setData] = useState(null)          // { kit, items }
  const [library, setLibrary] = useState(null)    // [{ id, name, source_kind, usedBy }] once "reuse" is opened
  const [picking, setPicking] = useState(false)
  const [isFeeder, setIsFeeder] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function load() {
    setError('')
    try {
      const [k, s] = await Promise.all([getTypeKit(typeId), listMilestoneTypeSettings().catch(() => ({ settings: {} }))])
      setData(k)
      setIsFeeder(((s.settings || {})[typeId] || {}).kind === 'feeder')
    } catch (e) { setError(String(e && e.message || e)) }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [typeId])
  useEffect(() => {
    const onKind = (e) => { if (!e.detail || e.detail.typeId === typeId) load() }
    window.addEventListener('milestone-type-kind-changed', onKind)
    return () => window.removeEventListener('milestone-type-kind-changed', onKind)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId])

  async function act(fn) {
    setBusy(true)
    try { const r = await fn(); if (r && 'items' in r) setData(r); else await load() }
    catch (e) { await alertDialog({ title: 'That did not save', message: String(e && e.message || e) }) }
    finally { setBusy(false) }
  }

  const defaultLabel = 'Before ' + (typeName || 'this milestone').replace(/[🔄🔹]/gu, '').trim()
  async function addNew() {
    const v = await promptDialog({ title: 'New item', message: 'What must be in hand? Say it as a fact about the project, the way you would to a new hire.', multiline: true, required: true })
    if (v === null) return
    setPicking(false)
    act(() => addKitFact(typeId, { name: v, sourceKind: 'setup', kitLabel: data && data.kit ? undefined : defaultLabel }))
  }
  async function openPicker() {
    setPicking(true)
    if (!library) { try { setLibrary((await getFactLibrary()).facts || []) } catch { setLibrary([]) } }
  }
  async function reword(it) {
    const v = await promptDialog({
      title: 'Reword the item', defaultValue: it.name, multiline: true, required: true,
      message: it.usedBy > 1 ? `This fact is shared by ${it.usedBy} milestone types. Rewording it changes it for all of them.` : undefined,
    })
    if (v !== null) act(() => updateKitFact(it.id, { name: v }))
  }
  function move(idx, dir) {
    const ids = data.items.map(i => i.id)
    const j = idx + dir
    if (j < 0 || j >= ids.length) return
    ;[ids[idx], ids[j]] = [ids[j], ids[idx]]
    act(() => reorderKit(typeId, ids))
  }

  const box = { margin: '10px 24px 0', padding: '10px 14px', borderRadius: 8, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', flexShrink: 0, fontFamily: 'DM Sans, sans-serif', fontSize: 12.5 }
  const head = <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--aj-text-muted)' }}>Needs before it starts</span>
  if (error) return <div style={box}>{head} <span role="alert" style={{ color: '#c0392b' }}>{error}</span></div>
  if (!data) return <div style={box}>{head} <span style={{ color: 'var(--aj-text-muted)' }}>Loading…</span></div>

  const kit = data.kit
  const items = data.items || []
  const inKit = new Set(items.map(i => i.id))
  const addRow = (
    <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <button style={S.btn} disabled={busy} onClick={addNew}>+ Write a new item</button>
      {!picking
        ? <button style={S.btn} disabled={busy} onClick={openPicker}>+ Use an existing one</button>
        : library === null
          ? <span style={{ color: 'var(--aj-text-muted)' }}>Loading the firm list…</span>
          : <Select searchable value="" placeholder="choose a fact from the firm list…" style={{ width: 420 }}
              onChange={v => { if (!v) return; setPicking(false); act(() => addKitFact(typeId, { factId: v, kitLabel: kit ? undefined : defaultLabel })) }}
              options={library.filter(f => !inKit.has(f.id)).map(f => ({ value: f.id, label: f.name + (f.usedBy ? `  · used by ${f.usedBy}` : '') }))} />}
    </div>
  )

  if (!kit || !items.length) {
    return (
      <div style={box}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          {head}
          <span style={{ color: 'var(--aj-text-muted)' }}>
            {isFeeder ? 'Nothing. A feeder carries no kit; it is owed to us, not started by us.' : 'Nothing yet. It can start with nothing in hand.'}
          </span>
        </div>
        {!isFeeder && addRow}
      </div>
    )
  }

  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        {head}
        <button style={{ ...S.linkBtn, fontWeight: 600 }} disabled={busy} onClick={async () => { const v = await promptDialog({ title: 'Kit name', defaultValue: kit.label || defaultLabel, required: true }); if (v !== null) act(() => saveTypeKit(typeId, { label: v })) }}>{kit.label || 'name it'}</button>
        <span style={{ color: 'var(--aj-text-muted)' }}>{items.length} item{items.length === 1 ? '' : 's'} must be in hand first. It blocks: the only way past a missing item is "not required", with a reason, and that counts for the whole project.</span>
        {!kit.active && <span style={S.off}>turned off</span>}
      </div>
      {isFeeder && (
        <div style={{ marginTop: 6, color: '#b9770e', fontSize: 12 }}>This type is a feeder, and a feeder carries no kit. Delete this kit, or switch the type back to our milestone.</div>
      )}
      <div style={{ ...S.fieldRow, gridTemplateColumns: '170px 1fr', marginTop: 4 }}>
        <span style={S.fieldLabel}>Why it matters</span>
        <button style={{ ...S.linkBtn, textAlign: 'left', whiteSpace: 'normal', fontSize: 12.5 }} disabled={busy} onClick={async () => { const v = await promptDialog({ title: 'Why this kit matters', message: 'One or two plain sentences: what it costs to start without it.', defaultValue: kit.description || '', multiline: true }); if (v !== null) act(() => saveTypeKit(typeId, { description: v })) }}>{kit.description || <em>write it</em>}</button>
      </div>
      <table style={{ ...S.table, fontSize: 12.5 }}>
        <thead><tr>
          <th style={{ ...S.th, width: 34 }}></th>
          <th style={{ ...S.th, textAlign: 'left' }}>What must be in hand</th>
          <th style={{ ...S.th, textAlign: 'left' }}>Who provides it</th>
          <th style={S.th} title="Arrives as a file: its Kit row offers Upload and links to what was received">File</th>
          <th style={S.th}></th>
        </tr></thead>
        <tbody>
          {items.map((it, idx) => (
            <tr key={it.id}>
              <td style={{ ...S.td, color: 'var(--aj-text-muted)' }}>
                <button style={S.arrow} disabled={busy || idx === 0} title="Move up" onClick={() => move(idx, -1)}>↑</button>
                <button style={S.arrow} disabled={busy || idx === items.length - 1} title="Move down" onClick={() => move(idx, 1)}>↓</button>
              </td>
              <td style={S.tdText}>
                <button style={{ ...S.linkBtn, textAlign: 'left', whiteSpace: 'normal', fontSize: 12.5 }} disabled={busy} onClick={() => reword(it)}>{it.name}</button>
                {it.usedBy > 1 && <span style={S.shared} title="A shared fact: in hand once per project, it counts for every milestone type that needs it"> · shared by {it.usedBy} types</span>}
              </td>
              <td style={S.td}>
                <select style={S.select} value={it.source_kind} disabled={busy} onChange={e => act(() => updateKitFact(it.id, { sourceKind: e.target.value }))}>
                  {GATE_SOURCE_KINDS.map(k => <option key={k} value={k}>{GATE_SOURCE_LABELS[k]}</option>)}
                </select>
              </td>
              <td style={{ ...S.td, textAlign: 'center' }}>
                <input type="checkbox" checked={!!it.expects_document} disabled={busy}
                  title={it.expects_document ? 'Arrives as a file (its Kit row offers Upload)' : 'Not a file'}
                  onChange={e => act(() => updateKitFact(it.id, { expectsDocument: e.target.checked }))} />
              </td>
              <td style={{ ...S.td, textAlign: 'right' }}>
                <button style={S.x} disabled={busy} title="Take this item off this type's kit (it stays in the firm list)" onClick={async () => { if (await confirmDialog({ title: 'Take this off the kit?', message: it.name + (it.usedBy > 1 ? '\n\nOther types that need it keep it.' : ''), confirmLabel: 'Take it off', danger: true })) act(() => removeKitFact(typeId, it.id)) }}>×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {addRow}
      <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
        <span style={{ flex: 1, color: 'var(--aj-text-muted)', fontSize: 11.5, alignSelf: 'center' }}>Changes apply right away, to every open milestone of this type. What a project already has in hand stays in hand.</span>
        <button style={S.btnQuiet} disabled={busy} onClick={() => act(() => saveTypeKit(typeId, { active: !kit.active }))}>{kit.active ? 'Turn this kit off' : 'Turn this kit on'}</button>
        <button style={S.btnDanger} disabled={busy} onClick={async () => { if (await confirmDialog({ title: 'Delete this kit?', message: 'Removes the kit from this type. The facts stay in the firm list, and projects keep what they have in hand.', confirmLabel: 'Delete kit', danger: true })) act(() => deleteTypeKit(typeId)) }}>Delete kit</button>
      </div>
    </div>
  )
}

export default TypeKitEditor

const S = {
  off: { fontFamily: 'DM Mono, monospace', fontSize: 11, color: '#c0392b', textTransform: 'uppercase', letterSpacing: '.05em' },
  fieldRow: { display: 'grid', gridTemplateColumns: '220px 1fr', gap: 12, padding: '6px 0', alignItems: 'start', fontSize: 13.5 },
  fieldLabel: { fontFamily: 'DM Mono, monospace', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--aj-text-muted)', paddingTop: 4 },
  linkBtn: { font: 'inherit', fontSize: 13.5, background: 'transparent', border: 0, padding: '2px 0', color: 'var(--aj-text)', cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 3, lineHeight: 1.45 },
  table: { borderCollapse: 'collapse', width: '100%', fontSize: 13.5, marginTop: 10 },
  th: { fontFamily: 'DM Mono, monospace', fontSize: 11, fontWeight: 500, color: 'var(--aj-text-muted)', padding: '6px 8px', borderBottom: '0.5px solid var(--aj-border)', textAlign: 'center', whiteSpace: 'nowrap' },
  tdText: { padding: '8px', borderBottom: '0.5px solid var(--aj-border)', verticalAlign: 'top', maxWidth: 560 },
  td: { padding: '8px', borderBottom: '0.5px solid var(--aj-border)', verticalAlign: 'top', whiteSpace: 'nowrap' },
  select: { font: 'inherit', fontSize: 13, padding: '3px 6px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)' },
  shared: { fontFamily: 'DM Mono, monospace', fontSize: 10.5, color: 'var(--aj-accent-blue)' },
  arrow: { font: 'inherit', fontSize: 11, background: 'transparent', border: 0, color: 'var(--aj-text-muted)', cursor: 'pointer', padding: '0 2px' },
  x: { font: 'inherit', fontSize: 16, lineHeight: 1, background: 'transparent', border: 0, color: 'var(--aj-text-muted)', cursor: 'pointer', padding: '0 4px' },
  btn: { fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, padding: '6px 12px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', cursor: 'pointer' },
  btnQuiet: { fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, padding: '6px 12px', borderRadius: 4, border: '0.5px solid var(--aj-border)', background: 'transparent', color: 'var(--aj-text-mid)', cursor: 'pointer' },
  btnDanger: { fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, padding: '6px 12px', borderRadius: 4, border: '0.5px solid rgba(192,57,43,0.5)', background: 'transparent', color: '#c0392b', cursor: 'pointer' },
}
